/**
 * ChatPanel — QueVee in the dock (desktop) or in More (phone).
 *
 * `docs/features/chat.md`. One component for both shells, Community's call: a
 * single column — the transcript, the composer, and a footer of New chat ·
 * History ▾ · Today: n of 50.
 *
 * **One socket per conversation.** The panel opens a `QueVeeClient` naming the
 * conversation it continues (none for a new chat); New chat and a History pick
 * close it and open another. quevee pins the vendor per conversation, so a
 * socket that changed conversation mid-flight would also be changing vendor
 * under a turn — reconnecting makes the boundary the socket's, where it is
 * obvious.
 *
 * **The tools are read at call time.** The manifest is sent once per socket,
 * but the app the agent acts on moves under it: a handler that closed over the
 * action context at connect would add an indicator to the chart that was
 * active then. Each registered tool delegates to `tools()` by name.
 *
 * **No Stop button.** The protocol has no cancel frame, and quevee's loop
 * cannot abandon a turn without leaving a `tool_use` unanswered — a history
 * the API refuses on the next message. While a turn runs, Send is disabled.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { SessionEnd } from "@qufolio/model";
import {
  QueVeeClient,
  type ClientTool,
  type QueVeeClientOptions,
} from "@qufolio/quevee-client";

import {
  chatSocketUrl,
  deleteConversation,
  fetchConversation,
  historyConfigured,
  listConversations,
  type ConversationSummary,
} from "../../api/chat";
import { idToken } from "../../lib/session";
import { ChatDisclaimerLine } from "./ChatDisclaimer";
import { QUEVEE_TOOLTIP, QueVeeMark } from "../QueVeeMark";
import { currentPass } from "../../lib/pass";
import {
  budgetLine,
  formatCost,
  formatWhen,
  toolLabel,
  transcriptFromStored,
  type ChatEntry,
  type ToolLine,
} from "../../lib/chat";

/** The part of `QueVeeClient` the panel uses — the seam a test fakes. */
export interface ChatClient {
  connect(): Promise<void>;
  send(text: string, context?: Record<string, unknown>): void;
  disconnect(): void;
}

export interface ChatPanelProps {
  /** The client tools as they are *now* — `clientTools(actionContext)`. */
  tools: () => ClientTool[];
  /**
   * A tool's title in the action registry, for its line; `undefined` for a
   * name that is not a client tool (a server one, which draws no line).
   */
  toolTitle?: (name: string) => string | undefined;
  /** What each turn tells the agent about the screen (`UserMessage.context`). */
  context?: () => Record<string, unknown> | undefined;
  /**
   * Ask AI's text, to send as soon as the panel can. A new `nonce` is a new
   * ask, so the same words twice are two messages.
   */
  ask?: { text: string; nonce: number };
  onClose?: () => void;
  /** Builds the socket client. A test passes a fake; the app, nothing. */
  createClient?: (options: QueVeeClientOptions) => ChatClient;
}

/**
 * The conversation the panel was last showing, kept across unmounts.
 *
 * Closing the dock half unmounts the panel; reopening it should continue where
 * it left off rather than start over. View state (D8), so module scope, not the
 * workspace — and not storage: a reload starting a fresh chat is fine, since
 * History ▾ is one click away.
 */
let lastConversation: string | null = null;

/** For tests: forget the remembered conversation. */
export function resetChatPanelMemory(): void {
  lastConversation = null;
}

function defaultClient(options: QueVeeClientOptions): ChatClient {
  return new QueVeeClient(options);
}

/** A fresh token and the groups pass on every open — `QueVeeClientOptions.auth`. */
async function authFrame(): Promise<{ token: string; pass?: string } | null> {
  const token = await idToken();
  if (!token) return null;
  const pass = currentPass();
  return pass ? { token, pass } : { token };
}

export function ChatPanel({ tools, toolTitle, context, ask, onClose, createClient = defaultClient }: ChatPanelProps) {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(lastConversation);
  const [budget, setBudget] = useState<{ used: number; limit: number } | undefined>(undefined);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<ConversationSummary[] | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  /**
   * Bumped to open a new socket: New chat, a History pick. The socket reads
   * `startFrom` at that moment, so a conversation id learned from a turn does
   * not reconnect anything — the client already knows it.
   */
  const [epoch, setEpoch] = useState(0);
  const startFrom = useRef<string | null>(lastConversation);
  const clientRef = useRef<ChatClient | null>(null);
  const toolsRef = useRef(tools);
  toolsRef.current = tools;
  const contextRef = useRef(context);
  contextRef.current = context;

  const remember = useCallback((id: string | null) => {
    lastConversation = id;
    setConversationId(id);
  }, []);

  /** The answer being written — the last entry, made if it is not an answer. */
  const onAnswer = useCallback((change: (entry: Extract<ChatEntry, { kind: "assistant" }>) => void) => {
    setEntries((list) => {
      const last = list[list.length - 1];
      const answer =
        last && last.kind === "assistant"
          ? { ...last, tools: [...last.tools] }
          : ({ kind: "assistant", text: "", tools: [] } as Extract<ChatEntry, { kind: "assistant" }>);
      change(answer);
      return last && last.kind === "assistant" ? [...list.slice(0, -1), answer] : [...list, answer];
    });
  }, []);

  const notice = useCallback((text: string) => {
    setEntries((list) => [...list, { kind: "notice", text }]);
  }, []);

  // ── the socket ─────────────────────────────────────────────────────────────
  useEffect(() => {
    // Registered once per socket; each delegates to the current list by name.
    const manifest: ClientTool[] = toolsRef.current().map((tool) => ({
      ...tool,
      handler: (args: Record<string, unknown>) =>
        (toolsRef.current().find((t) => t.name === tool.name) ?? tool).handler(args),
    }));
    const client = createClient({
      url: chatSocketUrl(),
      tools: manifest,
      auth: authFrame,
      ...(startFrom.current ? { conversationId: startFrom.current } : {}),
      listeners: {
        onTextDelta: ({ text }) => onAnswer((a) => (a.text += text)),
        onToolCall: (call) =>
          onAnswer((a) => {
            a.tools.push({ id: call.toolCallId, name: call.name, args: call.arguments, state: "running" });
          }),
        onToolResult: (outcome) =>
          onAnswer((a) => {
            a.tools = a.tools.map((line): ToolLine =>
              line.id === outcome.toolCallId
                ? {
                    ...line,
                    state: outcome.isError ? "refused" : "ok",
                    ...(outcome.errorMessage ? { reason: outcome.errorMessage } : {}),
                  }
                : line,
            );
          }),
        onTurnEnd: (end?: SessionEnd) => {
          setBusy(false);
          if (end?.usage) {
            const usage = end.usage;
            onAnswer((a) => {
              a.cost = {
                billedUsd: usage.billedUsd ?? null,
                inputTokens: usage.inputTokens,
                outputTokens: usage.outputTokens,
              };
            });
          }
          if (end?.conversationId) remember(end.conversationId);
          if (end?.budget) setBudget(end.budget);
        },
        onError: (error) => {
          if (error.source === "server") {
            notice(error.message);
            return;
          }
          if (error.source === "socket") setOffline(true);
        },
        onStatus: (status) => {
          if (status === "open") setOffline(false);
        },
      },
    });
    clientRef.current = client;
    void client.connect().catch(() => setOffline(true));
    return () => {
      client.disconnect();
      if (clientRef.current === client) clientRef.current = null;
      // A turn in flight dies with its socket; the answer stops where it was.
      setBusy(false);
    };
  }, [epoch, createClient, onAnswer, notice, remember]);

  const send = useCallback(
    (text: string) => {
      const typed = text.trim();
      const client = clientRef.current;
      if (!typed || !client || busy) return false;
      setEntries((list) => [...list, { kind: "user", text: typed }]);
      setBusy(true);
      client.send(typed, contextRef.current?.());
      return true;
    },
    [busy],
  );

  // Ask AI: sent once per nonce, however often the panel re-renders.
  const asked = useRef<number | null>(null);
  useEffect(() => {
    if (!ask || asked.current === ask.nonce) return;
    // Marked only once it went: asked mid-turn, it waits for Send to free up
    // (`send` changes with `busy`, which re-runs this) rather than vanishing.
    if (send(ask.text)) asked.current = ask.nonce;
  }, [ask, send]);

  // ── conversations ──────────────────────────────────────────────────────────
  const newChat = useCallback(() => {
    remember(null);
    startFrom.current = null;
    setEntries([]);
    setHistoryOpen(false);
    setEpoch((n) => n + 1);
  }, [remember]);

  /**
   * Show a saved conversation, and (unless the socket already names it) open
   * one that continues it. The transcript is read from qv-data, not the
   * socket: the socket carries turns from here on, never the past.
   */
  const resume = useCallback(
    async (id: string, reconnect = true) => {
      setHistoryOpen(false);
      try {
        const stored = await fetchConversation(id);
        setEntries(transcriptFromStored(stored.messages, (name) => !toolTitle || toolTitle(name) !== undefined));
        if (reconnect) {
          remember(id);
          startFrom.current = id;
          setEpoch((n) => n + 1);
        }
      } catch (cause) {
        notice(`That conversation could not be opened: ${cause instanceof Error ? cause.message : String(cause)}`);
      }
    },
    [notice, remember, toolTitle],
  );

  // Reopened on a remembered conversation: show what it said. The first
  // socket already continues it (`startFrom`), so nothing reconnects.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (startFrom.current && historyConfigured()) void resume(startFrom.current, false);
    // Once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!historyOpen) return;
    let alive = true;
    setHistory(null);
    void listConversations().then((page) => {
      if (alive) setHistory(page.conversations);
    });
    return () => {
      alive = false;
    };
  }, [historyOpen]);

  // **The list closes three ways**: its own Close, Esc, and a click outside it
  // (the History toggle excepted, which closes it itself). Its only ✕ used to
  // be Delete, so there was no way out but picking a chat (owner, 2026-10-19).
  const historyRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const closeHistory = useCallback(() => {
    setHistoryOpen(false);
    setConfirming(null);
  }, []);
  useEffect(() => {
    if (!historyOpen) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeHistory();
    };
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || historyRef.current?.contains(target) || toggleRef.current?.contains(target)) return;
      closeHistory();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [historyOpen, closeHistory]);

  const remove = useCallback(
    async (id: string) => {
      setConfirming(null);
      try {
        await deleteConversation(id);
        setHistory((list) => list?.filter((c) => c.id !== id) ?? null);
        if (id === conversationId) newChat();
      } catch (cause) {
        notice(`Could not delete it: ${cause instanceof Error ? cause.message : String(cause)}`);
      }
    },
    [conversationId, newChat, notice],
  );

  // ── view ───────────────────────────────────────────────────────────────────
  const scroller = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const box = scroller.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [entries]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter is a new line — and an IME composing a word
    // owns its Enter.
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (send(draft)) setDraft("");
  };

  const today = budgetLine(budget);
  const label = useMemo(() => (line: ToolLine) => toolLabel(line.name, line.args, toolTitle?.(line.name)), [toolTitle]);

  return (
    <div style={SHELL} aria-label="QueVee chat">
      <div style={HEADER}>
        <QueVeeMark size={24} title={QUEVEE_TOOLTIP} playOnClick thinking={busy} />
        <b style={{ fontSize: 13 }} title={QUEVEE_TOOLTIP}>AI</b>
        <span style={{ flex: 1 }} />
        {offline && <span style={{ ...MUTED, fontSize: 11 }}>reconnecting…</span>}
        {onClose && (
          <button type="button" style={ICON} title="Close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        )}
      </div>

      <div ref={scroller} style={TRANSCRIPT} role="log" aria-live="polite">
        {entries.length === 0 && (
          <div style={{ ...MUTED, padding: "12px 4px" }}>
            Ask about this chart or screen — "add a 21 sma and show weekly".
          </div>
        )}
        {entries.map((entry, i) => (
          <Entry key={i} entry={entry} label={label} live={busy && i === entries.length - 1} />
        ))}
      </div>

      <div style={COMPOSER}>
        <textarea
          aria-label="Message"
          placeholder="Ask about this chart or screen…"
          value={draft}
          rows={2}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          style={INPUT}
        />
        <button
          type="button"
          style={SEND}
          disabled={busy || !draft.trim()}
          onClick={() => {
            if (send(draft)) setDraft("");
          }}
        >
          Send
        </button>
      </div>

      <div style={FOOTER}>
        <button type="button" style={LINK} onClick={newChat}>
          New chat
        </button>
        {historyConfigured() && (
          <>
            <span style={MUTED}>·</span>
            <button
              ref={toggleRef}
              type="button"
              style={LINK}
              aria-expanded={historyOpen}
              onClick={() => (historyOpen ? closeHistory() : setHistoryOpen(true))}
            >
              History ▾
            </button>
          </>
        )}
        {today && (
          <>
            <span style={MUTED}>·</span>
            <span style={MUTED}>{today}</span>
          </>
        )}
      </div>

      {historyOpen && (
        <div ref={historyRef} style={HISTORY} role="menu" aria-label="Saved conversations">
          <div style={HISTORY_HEAD}>
            <span style={MUTED}>Saved chats</span>
            <button type="button" style={ICON} aria-label="Close saved chats" title="Close (Esc)" onClick={closeHistory}>
              ✕
            </button>
          </div>
          {history === null && <div style={{ ...MUTED, padding: 8 }}>Loading…</div>}
          {history !== null && history.length === 0 && <div style={{ ...MUTED, padding: 8 }}>No saved conversations yet.</div>}
          {history?.map((row) => (
            <div key={row.id} style={{ ...HISTORY_ROW, ...(row.id === conversationId ? CURRENT : {}) }}>
              {confirming === row.id ? (
                <>
                  <span style={{ flex: 1 }}>Delete “{row.title}”?</span>
                  <button type="button" style={DANGER} onClick={() => void remove(row.id)}>
                    Delete
                  </button>
                  <button type="button" style={LINK} onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    style={{ ...LINK, flex: 1, textAlign: "left", minWidth: 0 }}
                    onClick={() => void resume(row.id)}
                  >
                    <span style={TITLE}>{row.title}</span>
                    <span style={{ ...MUTED, fontSize: 11 }}>{formatWhen(row.updatedAt)}</span>
                  </button>
                  <button
                    type="button"
                    style={ICON}
                    title="Delete this conversation"
                    aria-label={`Delete ${row.title}`}
                    onClick={() => setConfirming(row.id)}
                  >
                    {/* A bin, not ✕: ✕ is the list's Close above. */}
                    🗑
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Entry({ entry, label, live }: { entry: ChatEntry; label: (line: ToolLine) => string; live: boolean }) {
  if (entry.kind === "user") return <Row who="You">{entry.text}</Row>;
  if (entry.kind === "notice") return <div style={NOTICE}>{entry.text}</div>;
  return (
    <Row who={<QueVeeMark size={22} title={QUEVEE_TOOLTIP} thinking={live} />}>
      {entry.text || (live ? <span style={MUTED}>…</span> : null)}
      {entry.tools.map((line) => (
        <div key={line.id} style={TOOL_LINE}>
          <span style={{ color: line.state === "refused" ? "var(--qf-negative)" : line.state === "ok" ? "var(--qf-positive)" : "var(--qf-text-muted)" }}>
            {line.state === "refused" ? "✗" : line.state === "ok" ? "✓" : "…"}
          </span>{" "}
          {label(line)}
          {line.state === "refused" && line.reason ? <span style={MUTED}> — {line.reason}</span> : null}
        </div>
      ))}
      {entry.cost && <div style={COST}>{formatCost(entry.cost)}</div>}
      {/* Under every finished answer, drawn here rather than asked of the
          model: a sentence the model writes can be dropped or reworded, and
          costs tokens every turn (owner, 2026-10-19). */}
      {!live && <ChatDisclaimerLine />}
    </Row>
  );
}

function Row({ who, children }: { who: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 6, padding: "4px 2px", alignItems: "baseline" }}>
      <b style={{ flex: "0 0 auto", width: 30, color: "var(--qf-text-secondary)", fontSize: 12 }}>{typeof who === "string" ? `${who}:` : who}</b>
      <div style={{ flex: 1, minWidth: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}

const SHELL: React.CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  height: "100%",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  fontSize: 13,
};
const HEADER: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
};
const TRANSCRIPT: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 8px" };
const COMPOSER: React.CSSProperties = {
  display: "flex",
  gap: 6,
  padding: "6px 8px",
  borderTop: "1px solid var(--qf-border)",
  alignItems: "flex-end",
};
const INPUT: React.CSSProperties = {
  flex: 1,
  resize: "none",
  font: "inherit",
  padding: "5px 7px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-background)",
  color: "var(--qf-text)",
};
const SEND: React.CSSProperties = {
  padding: "6px 12px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-accent)",
  color: "var(--qf-background)",
  font: "inherit",
  cursor: "pointer",
};
const FOOTER: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "2px 8px 6px",
  fontSize: 12,
};
const LINK: React.CSSProperties = {
  border: 0,
  background: "transparent",
  color: "var(--qf-accent)",
  font: "inherit",
  padding: 0,
  cursor: "pointer",
};
const DANGER: React.CSSProperties = { ...LINK, color: "var(--qf-negative)" };
const ICON: React.CSSProperties = { ...LINK, color: "var(--qf-text-secondary)", padding: "0 4px" };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)" };
const NOTICE: React.CSSProperties = {
  margin: "4px 2px",
  padding: "4px 8px",
  borderLeft: "2px solid var(--qf-negative)",
  color: "var(--qf-text-secondary)",
  fontSize: 12,
};
const TOOL_LINE: React.CSSProperties = { fontSize: 12, color: "var(--qf-text-secondary)", marginTop: 2 };
const COST: React.CSSProperties = { fontSize: 11, color: "var(--qf-text-muted)", marginTop: 3 };
const HISTORY: React.CSSProperties = {
  position: "absolute",
  left: 8,
  right: 8,
  bottom: 30,
  maxHeight: "60%",
  overflowY: "auto",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
  zIndex: 5,
};
const HISTORY_HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  fontSize: 11,
};
const HISTORY_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 8px",
  borderBottom: "1px solid var(--qf-border)",
};
const CURRENT: React.CSSProperties = { background: "var(--qf-surface-alt)" };
const TITLE: React.CSSProperties = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--qf-text)",
};
